import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

type AcikIs = { domain: string; description: string; count: number; blocking: boolean };

type Onizleme = {
  canLeave: boolean;
  blockedReason: "last_admin" | "blocking_responsibility" | "not_active" | null;
  consequences: string[];
  openResponsibilities: AcikIs[];
};

/** Engel sebebinin kullanıcıya ne anlattığı — metin arayüzde, sebep sunucuda. */
const ENGEL_METNI: Record<NonNullable<Onizleme["blockedReason"]>, string> = {
  last_admin: "Bu organizasyonun son yöneticisi olduğunuz için ayrılamıyorsunuz.",
  blocking_responsibility: "Devredilemeyen açık işleriniz olduğu için ayrılamıyorsunuz.",
  not_active: "Üyeliğiniz aktif olmadığı için ayrılamıyorsunuz.",
};

/**
 * ORGANİZASYONDAN AYRILMA (CLOS-78 `[041]`).
 *
 * ⚠ Ekranı yoktu: üye kendi iradesiyle ayrılamıyor, yöneticiye başvurmak zorunda
 * kalıyordu. Ön izleme ucu da hazırdı ve hiç çağrılmıyordu.
 *
 * ⚠ Sonuçlar ve açık işler ayrılmadan ÖNCE okunuyor. Geri alınamaz bir işlem sürprize yer
 * bırakmamalı: kullanıcı neyi kaybedeceğini ve arkasında ne bıraktığını görüyor.
 *
 * ⚠ İkinci onay adımı var. Tek tıklamayla geri alınamaz bir işlem yapılmıyor — geri dönüş
 * yalnız yeniden davetle mümkün.
 *
 * ⚠ Sunucu engelliyorsa düğme SUNULMUYOR ve sebebi yazılıyor. Düğmeyi gösterip reddi
 * kullanıcıya denettirmek, yapamayacağı bir şeyi denemeye davet etmek olurdu.
 */
export function LeaveOrganizationSection() {
  const { activeTenantId, activeTenant } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const navigate = useNavigate();
  const [onayAcik, setOnayAcik] = useState(false);

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "ayrilma-onizleme"),
    queryFn: async () => (await api<Onizleme>(`/api/tenants/${tenantId}/leave`, { tenantId })).data,
  });

  const ayril = useMutation({
    mutationFn: async () =>
      (await api(`/api/tenants/${tenantId}/leave`, { method: "POST", tenantId })).data,
    onSuccess: () => {
      toast.success("Organizasyondan ayrıldınız");
      /** Ayrıldıktan sonra bu alan artık yok: kullanıcı çalışma alanı seçimine dönüyor. */
      void navigate("/tenants");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Ayrılma tamamlanamadı");
    },
  });

  if (sorgu.isPending) {
    return (
      <Card title="Organizasyondan Ayrıl">
        <div role="status" aria-label="Ayrılma bilgisi yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  const onizleme = sorgu.data;
  const engel = onizleme?.blockedReason ?? null;

  return (
    <Card title="Organizasyondan Ayrıl">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-soft">
          {activeTenant?.name ?? "Bu organizasyon"} üyeliğinizi kendiniz sonlandırabilirsiniz.
        </p>

        {engel ? (
          <p role="alert" className="text-sm text-danger">
            {ENGEL_METNI[engel]}
          </p>
        ) : null}

        <ul className="flex flex-col gap-1 text-sm text-fg">
          {(onizleme?.consequences ?? []).map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>

        {(onizleme?.openResponsibilities ?? []).length > 0 ? (
          <div className="flex flex-col gap-1">
            <p className="text-xs text-fg-muted">Açık işleriniz</p>
            <ul className="flex flex-col gap-1">
              {(onizleme?.openResponsibilities ?? []).map((is) => (
                <li key={`${is.domain}-${is.description}`} className="flex items-center gap-2">
                  {/* Engelleyen iş ile devredilebilir iş aynı görünmüyor: karar farklı. */}
                  <Badge tone={is.blocking ? "danger" : "neutral"}>
                    {is.blocking ? "Engelliyor" : "Devredilebilir"}
                  </Badge>
                  <span className="text-sm text-fg">{is.description}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {onizleme?.canLeave ? (
          onayAcik ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-auto text-sm text-fg-strong">
                Ayrılmak istediğinize emin misiniz? Bu işlem geri alınamaz.
              </span>
              <Button type="button" variant="secondary" onClick={() => setOnayAcik(false)}>
                Vazgeç
              </Button>
              <Button type="button" loading={ayril.isPending} onClick={() => ayril.mutate()}>
                Ayrılmayı onayla
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button type="button" variant="secondary" onClick={() => setOnayAcik(true)}>
                Organizasyondan ayrıl
              </Button>
            </div>
          )
        ) : null}
      </div>
    </Card>
  );
}
